import type { BlockResolver } from "./btc-explorer";
import { verifyFileHash } from "./hash";
import { verifyInclusion } from "./merkle";
import { verifyOTSAttestation } from "./ots";
import { verifyTSAEntry } from "./tsa";
import type {
  CheckResult,
  FileVouchReceipt,
  VerificationResult,
} from "./types";

export type VerifyOptions = {
  // Optional online Bitcoin block resolver. When supplied, the bitcoin
  // check runs the full chain-confirmation path: the OTS proof's terminal
  // value is compared against block N's actual merkle_root. Without a
  // resolver, only offline checks run. Pass `mempoolSpaceResolver` from
  // this package for the canonical default.
  resolveBlock?: BlockResolver;
};

// Top-level offline verifier. Returns a per-check `VerificationResult`
// covering everything that can be checked without external lookups:
//
//   1. hash    — file's SHA-256 matches receipt.subject.hash.
//   2. merkle  — when present, the RFC 6962 audit path reconstructs root.
//   3. tsa     — for every TSA entry: PKIStatus, hash algorithm OID,
//                messageImprint, nonce echo, AND the CMS SignedData
//                signature math (RSASSA-PKCS1 / ECDSA P-256). Trust-chain
//                validation against public CA roots is a future slice.
//   4. bitcoin — when status="confirmed": OTS proof parses, walks ops
//                from receipt.merkle.root, contains a BTC attestation at
//                receipt.bitcoin.block. Online chain confirmation (block
//                N exists with matching merkle root) is a future slice.
//
// `ok` is the AND across every applicable check. Each per-check field is
// independently inspectable so a UI can show partial verification state.
export async function verifyReceipt(
  receipt: FileVouchReceipt,
  fileBytes: Uint8Array,
  options?: VerifyOptions,
): Promise<VerificationResult> {
  const hash = await checkHash(receipt, fileBytes);

  let merkle: CheckResult | null = null;
  if (receipt.merkle) {
    merkle = await checkMerkle(
      receipt as FileVouchReceipt & {
        merkle: NonNullable<FileVouchReceipt["merkle"]>;
      },
    );
  }

  const tsa = await checkTsa(receipt);
  const bitcoin = await checkBitcoin(receipt, options?.resolveBlock);

  const ok =
    hash.ok &&
    (merkle === null || merkle.ok) &&
    (tsa === null || tsa.ok) &&
    (bitcoin === null || bitcoin.ok);

  return { ok, hash, merkle, tsa, bitcoin };
}

async function checkHash(
  receipt: FileVouchReceipt,
  fileBytes: Uint8Array,
): Promise<CheckResult> {
  if (receipt.subject.hashAlgorithm !== "sha256") {
    return {
      ok: false,
      reason: `unsupported hash algorithm: ${receipt.subject.hashAlgorithm}`,
    };
  }
  const matches = await verifyFileHash(fileBytes, receipt.subject.hash);
  return matches
    ? { ok: true }
    : {
        ok: false,
        reason: "file hash does not match receipt.subject.hash",
      };
}

async function checkMerkle(
  receipt: FileVouchReceipt & { merkle: NonNullable<FileVouchReceipt["merkle"]> },
): Promise<CheckResult> {
  const valid = await verifyInclusion(
    receipt.subject.hash,
    receipt.merkle.path,
    receipt.merkle.root,
  );
  return valid
    ? { ok: true }
    : {
        ok: false,
        reason: "merkle audit path does not reconstruct the declared root",
      };
}

// Verify the receipt's Bitcoin anchor when status is "confirmed". Returns
// null for "pending" / "none" — those aren't failures, just pre-anchor
// states. Offline check: parse the OTS proof, walk operations from the
// receipt's merkle.root, confirm a BTC attestation with the matching
// block height. Online chain-existence verification is out of scope here
// and lives in the verifier UI (block explorer query).
async function checkBitcoin(
  receipt: FileVouchReceipt,
  resolveBlock?: BlockResolver,
): Promise<CheckResult | null> {
  const btc = receipt.bitcoin;
  if (btc.status !== "confirmed") return null;
  if (!receipt.merkle) {
    return {
      ok: false,
      reason: "bitcoin status is confirmed but receipt has no merkle.root",
    };
  }
  return verifyOTSAttestation(btc.otsProof, receipt.merkle.root, btc.block, {
    ...(resolveBlock !== undefined ? { resolveBlock } : {}),
  });
}

// Verify every TSA entry in the receipt. Returns null if no TSA evidence is
// present (anon receipts may have an empty tsa map; that's a legitimate
// state pre-Block-3-paid-tier-TSA). All entries must pass for the
// aggregate to be ok.
async function checkTsa(
  receipt: FileVouchReceipt,
): Promise<CheckResult | null> {
  const signers = Object.keys(receipt.tsa);
  if (signers.length === 0) return null;

  const failures: string[] = [];
  for (const signer of signers) {
    const entry = receipt.tsa[signer]!;
    const result = await verifyTSAEntry(entry, receipt.subject.hash);
    if (!result.ok) failures.push(`${signer}: ${result.reason}`);
  }
  // Content + CMS signature + cert chain to embedded trust root all checked.
  return failures.length === 0
    ? {
        ok: true,
        note: `${signers.length} TSA entry/entries fully verified (content, signature, chain to trust root)`,
      }
    : { ok: false, reason: failures.join("; ") };
}
